/**
 * 商品分類管理 API 服務
 * 使用 RTK Query 封裝商品分類相關的 API 調用
 * 
 * 注意：目前使用 mock 數據進行開發測試
 * 待 API 整合後，將 mockBaseQuery 替換為 baseQuery
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';

import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  IProductCategory,
  ICategoryListParams,
  ICategoryFormData,
  IBatchUpdateCategoriesRequest,
  IBatchUpdateCategoriesResponse,
  IBatchCategorySortRequest,
  ICategoryStatistics,
} from '@/types/productCategory';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 商品分類 API 服務
 */
export const productCategoryApi = createApi({
  reducerPath: 'productCategoryApi',
  baseQuery: baseQueryWithErrorHandling,
  // baseQuery, // 真實 API baseQuery（待 API 整合後使用）
  tagTypes: ['ProductCategory', 'CategoryStatistics'],
  endpoints: (builder) => ({
    // 獲取分類列表
    getCategories: builder.query<
      IApiResponse<IPaginatedResponse<IProductCategory>>,
      ICategoryListParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_CATEGORIES,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.level && params.level !== 'all' && { level: params.level }),
          ...(params.parent_id !== undefined && { parent_id: params.parent_id }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['ProductCategory'],
    }),

    // 獲取分類詳情
    getCategoryById: builder.query<IApiResponse<IProductCategory>, string>({
      query: (id) => ({
        url: ConstantUtil.API_CATEGORY_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'ProductCategory', id }],
    }),

    // 創建分類
    createCategory: builder.mutation<IApiResponse<IProductCategory>, ICategoryFormData>({
      query: (data) => ({
        url: ConstantUtil.API_CATEGORY_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ProductCategory', 'CategoryStatistics'],
    }),

    // 更新分類
    updateCategory: builder.mutation<
      IApiResponse<IProductCategory>,
      { id: string; data: Partial<ICategoryFormData> }
    >({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_CATEGORY_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ProductCategory', id },
        'ProductCategory',
        'CategoryStatistics',
      ],
    }),

    // 刪除分類（軟刪除）
    deleteCategory: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_CATEGORY_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['ProductCategory', 'CategoryStatistics'],
    }),

    // 批量更新分類
    batchUpdateCategories: builder.mutation<
      IApiResponse<IBatchUpdateCategoriesResponse>,
      IBatchUpdateCategoriesRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_CATEGORY_BATCH_UPDATE,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['ProductCategory', 'CategoryStatistics'],
    }),

    // 批量排序分類
    batchSortCategories: builder.mutation<
      IApiResponse<{ success: number; failed: number }>,
      IBatchCategorySortRequest
    >({
      query: (data) => ({
        url: ConstantUtil.API_CATEGORY_BATCH_SORT,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['ProductCategory'],
    }),

    // 獲取分類統計
    getCategoryStatistics: builder.query<IApiResponse<ICategoryStatistics>, void>({
      query: () => ({
        url: ConstantUtil.API_CATEGORY_STATISTICS,
        method: 'GET',
      }),
      providesTags: ['CategoryStatistics'],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useBatchUpdateCategoriesMutation,
  useBatchSortCategoriesMutation,
  useGetCategoryStatisticsQuery,
} = productCategoryApi;

